<template>
  <Loader :value="promise || !hasLoaded" class="custom-field-projects-list">
    <template v-if="parentIsActive">
      <div v-if="filterOptions && showFilter" class="row mb-2">
        <FilterComponent v-model="filter" v-bind="filterOptions" />
      </div>
      <List v-show="result.length" v-bind="listOptions.props" v-on="listOptions.listeners" />
      <EmptyState
        v-show="!result.length"
        :title="$t('noX', { x: title || this.$tc('project', 2).toLowerCase() })"
        :icon="['fal', 'drafting-compass']"
      />
    </template>
  </Loader>
</template>

<script>
import fetch from '@/fetch'

import { LoaderComponent as Loader } from 'vue-elder-loader'
import List from '@/components/Project/List/Component'
import FilterComponent from '@/components/Filter/Component'
import { Sort } from '@/components/Sort'
import { ObjectToDotNotation } from '@/utils'
import EmptyState from '@/components/EmptyState'
import FilterMixin from '@/components/Filter'
import tracker from '@/tracking'
import { Debounce } from '@/utils'

export default {
  mixins: [
    FilterMixin({
      default: { search: '' },
    }),
  ],
  name: 'ProjectList',
  props: {
    title: String,
    value: {
      type: Array,
      default: () => [],
    },
    items: [String, Array],
    config: Array,
    parentIsActive: Boolean,
    selectable: {
      type: Boolean,
      default: true,
    },
    wrappers: {
      type: Object,
      default: () => ({}),
    },
    showFilter: {
      type: Boolean,
      default: true,
    },
    actions: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    items: {
      handler: 'getItems',
      deep: true,
      immediate: true,
    },
    filter: {
      handler() {
        this.getItems('filter')
      },
      deep: true,
    },
    sort: {
      handler() {
        this.getItems('sort')
      },
      deep: true,
    },
  },

  data() {
    return {
      promise: null,
      result: [],
      hasLoaded: false,

      sort: {},
      // generate debounce per instance — so multiple ProjectLists don't cancel each other's refetch
      debouncedLoad: Debounce(v => this.load(v), 300),
    }
  },
  methods: {
    load(v) {
      if (v) {
        if (Object.values(this[v] || {}).filter(v => (Array.isArray(v) ? v.length : Boolean(v))).length)
          tracker.emit(`ProjectList:${v}`, { ...(this[v] || {}) })
      }

      let [sortKey, direction] = Object.entries(this.sort)[0] || []

      if (this.items instanceof Array) {
        this.result = sortKey ? Sort(this.items, sortKey, direction) : this.items
        this.hasLoaded = true
        return
      }

      const endpoint = this.items.match(/^https?:\/{2}/) ? this.items : `${location.origin}${this.items}`
      const url = new URL(endpoint)

      Object.entries(ObjectToDotNotation(this.filter))
        .filter(([k, v]) => ![null, undefined].includes(v))
        .forEach(([k, v]) => {
          if (v instanceof Array && !v.length) return
          url.searchParams.append(k, v)
        })

      if (sortKey) url.searchParams.append(`sort.${sortKey}`, direction)

      this.promise = fetch(url.toString())
        .then(res => res.json())
        .then(async items => {
          this.result = items
          this.hasLoaded = true

          return this.$nextTick()
        })
    },
    getItems(v) {
      return this.debouncedLoad(v)
    },
    refetch() {
      this.load()
    },
  },
  computed: {
    listOptions() {
      return {
        props: {
          value: this.result,
          selected: this.selectable ? this.value && this.result.filter(({ id }) => this.value.includes(id)) : undefined,
          config: this.config.filter(({ id }) => id).map(({ filter, ...rest }) => rest),
          filter: this.filter,
          ...this.sortOptions.props,
          wrappers: this.wrappers,
          actions: this.actions,
        },
        listeners: {
          ['update:selected']: v =>
            this.$emit(
              'input',
              v.map(({ id }) => id),
            ),
          ...this.sortOptions.listeners,
        },
      }
    },
    sortableBy() {
      return this.config.filter(({ sort }) => sort).map(({ id }) => id)
    },
    sortOptions() {
      const props = {}
      const listeners = {}

      if (this.sortableBy.length) {
        props.sortableBy = this.sortableBy
        props.sortBy = this.sort

        listeners['update:sortBy'] = v => (this.sort = v)
      }

      return { props, listeners }
    },
    filterOptions() {
      if (typeof this.items !== 'string') return

      const filters = this.config.filter(({ filter }) => filter).map(({ filter }) => filter)
      return {
        items: filters.map(({ id }) => id),
        default: Object.fromEntries(
          filters.filter(({ default: isDefault }) => isDefault).map(({ id }) => [id.split(':').pop(), null]),
        ),
        options: Object.fromEntries(
          filters.map(({ id, label, options = {} }) => [
            id.split(':').pop(),
            { ...options, label: label || options.label || id },
          ]),
        ),
      }
    },
  },
  created() {
    if (!this.value) this.$emit('input', [])
    this.$bus.$on('project-list:refetch', this.refetch)
  },
  beforeDestroy() {
    this.$bus.$off('project-list:refetch', this.refetch)
  },
  components: {
    EmptyState,
    List,
    Loader,
    FilterComponent,
  },
}
</script>

<style lang="scss">
.custom-field-projects-list {
  .data-table__footer__content {
    display: none;
  }
}
</style>
